import moment from 'moment';

import {
  REFERRAL_DATE_FIELD_NAMES,
  REFERRAL_FORM_FIELD_NAMES,
  REFERRAL_INITIAL_VALUES,
  REFERRAL_LIST_FIELD_NAMES,
} from './constants';
import { REFERRAL_FORM_SCHEMA } from '../validationSchema';
import { API_DATE_FORMAT } from '../../../../../../../constants';

import type { Moment } from 'moment';
import type {
  ReferralDateFieldName,
  ReferralFieldName,
  ReferralFieldValue,
  ReferralListFieldName,
  ReferralPatchData,
  ReferralSavedFieldValue,
  ReferralSavedFieldValues,
} from './types';
import type { ReferralFormValues } from '../../../../types';
import type { IOnboardingQuestionnaireReferralInfo } from '../types';

function getMomentValue(value: string | null | undefined): Moment | null {
  if (!value) {
    return null;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
}

export function getReferralInitialValues(
  referralInfo: IOnboardingQuestionnaireReferralInfo | null | undefined,
): ReferralFormValues {
  if (referralInfo == null) {
    return REFERRAL_INITIAL_VALUES;
  }

  return {
    referral_physician_name: referralInfo.referral_physician_name ?? '',
    diagnosis_date: getMomentValue(referralInfo.diagnosis_date),
    diagnosis_city: referralInfo.diagnosis_city ?? '',
    diagnosis_state: referralInfo.diagnosis_state ?? '',
    diagnostic_assessments_used: [...(referralInfo.diagnostic_assessments_used ?? [])],
    diagnoses: [...(referralInfo.diagnoses ?? [])],
  };
}

export function isReferralDateFieldName(name: ReferralFieldName): name is ReferralDateFieldName {
  return REFERRAL_DATE_FIELD_NAMES.some((fieldName) => fieldName === name);
}

export function isReferralListFieldName(name: ReferralFieldName): name is ReferralListFieldName {
  return REFERRAL_LIST_FIELD_NAMES.some((fieldName) => fieldName === name);
}

export function getSavedFieldValue(
  name: ReferralFieldName,
  values: ReferralFormValues,
): ReferralSavedFieldValue {
  if (isReferralDateFieldName(name)) {
    return values[name]?.format(API_DATE_FORMAT) ?? '';
  }

  if (isReferralListFieldName(name)) {
    return [...values[name]];
  }

  const fieldValue = values[name];
  return fieldValue == null ? '' : String(fieldValue);
}

export function getSavedFieldValues(values: ReferralFormValues): ReferralSavedFieldValues {
  return REFERRAL_FORM_FIELD_NAMES.reduce<ReferralSavedFieldValues>(
    (savedValues, fieldName) => ({
      ...savedValues,
      [fieldName]: getSavedFieldValue(fieldName, values),
    }),
    {},
  );
}

export function getValuesWithFieldOverride(
  values: ReferralFormValues,
  name: ReferralFieldName,
  value: ReferralFieldValue | undefined,
): ReferralFormValues {
  if (value === undefined) {
    return values;
  }

  return { ...values, [name]: value };
}

export function getPatchData(
  name: ReferralFieldName,
  values: ReferralFormValues,
): ReferralPatchData {
  const data: ReferralPatchData = {};

  switch (name) {
    case 'referral_physician_name':
      data.referral_physician_name = values.referral_physician_name;
      return data;
    case 'diagnosis_date':
      data.diagnosis_date = values.diagnosis_date?.format(API_DATE_FORMAT) ?? null;
      return data;
    case 'diagnosis_city':
      data.diagnosis_city = values.diagnosis_city;
      return data;
    case 'diagnosis_state':
      data.diagnosis_state = values.diagnosis_state;
      return data;
    case 'diagnostic_assessments_used':
      data.diagnostic_assessments_used = [...values.diagnostic_assessments_used];
      return data;
    case 'diagnoses':
      data.diagnoses = [...values.diagnoses];
      return data;
  }
}

export function isReferralDateFieldValid(
  name: ReferralFieldName,
  values: ReferralFormValues,
): boolean {
  if (!isReferralDateFieldName(name)) {
    return true;
  }

  const value = values[name];
  return value == null || value.isValid();
}

export async function isReferralFieldSchemaValid(
  name: ReferralFieldName,
  values: ReferralFormValues,
): Promise<boolean> {
  try {
    await REFERRAL_FORM_SCHEMA.validateAt(name, values);
    return true;
  } catch {
    return false;
  }
}

export function areSavedValuesEqual(
  current: ReferralSavedFieldValue | undefined,
  next: ReferralSavedFieldValue,
): boolean {
  if (Array.isArray(current) && Array.isArray(next)) {
    if (current.length !== next.length) {
      return false;
    }
    return current.every((value, index) => value === next[index]);
  }

  return current === next;
}
